import { jsx as _jsx } from "react/jsx-runtime";
// MechanicIcon.tsx — a mechanic palette tile's picture: the item drawn with the SAME art the board uses
// (cellArt / itemSprites, i.e. the game's own sprites), so the palette and the board can never disagree about
// what a tool places. Multi-cell items (gate, door, bolt, laser, metal box, lock, connect) are drawn across
// their natural span and scaled down to fit the tile.
import { Component, createRef } from "react";
import { drawCleared, drawConnectorBar, drawEditorCell, drawGateWide, drawIceCover, drawMetalBox, drawElevator, drawGlassPanel, drawMetalRect, drawSlidingDoor, drawSuperMysteryCover, drawSuperTunnel, drawToolBox, drawPipeIcon, drawPropeller, } from "../art/cellArt";
import { drawBoltSprite, drawLaserGateSprite } from "../art/itemSprites";
import { onSpritesLoaded } from "../art/sprites";
import { makeCell } from "./templateOps";
import { GridDirection } from "../Models/CellData";
import { MarbleColor } from "../Models/LevelData";
// Draws mechanic `id` into a `w` x `h` box — wider than tall, so the long items (a gate, a door, a bolt, a
// laser) get the width their art needs while a one-cell item keeps the height.
function drawIcon(ctx, id, w, h) {
    // One cell filling the height, centred, with a little air round it.
    const one = (draw) => {
        const S = h * 0.94;
        ctx.save();
        ctx.translate((w - S) / 2, (h - S) / 2);
        draw(S);
        ctx.restore();
    };
    // A strip `n` cells wide across the full width, one cell tall, centred vertically.
    const strip = (n, draw) => {
        const cs = w / n;
        ctx.save();
        ctx.translate(0, (h - cs) / 2);
        draw(cs);
        ctx.restore();
    };
    // A 2x2 area filling the height.
    const quad = (draw) => {
        const cs = h * 0.47;
        ctx.save();
        ctx.translate(w / 2 - cs, h / 2 - cs);
        draw(cs);
        ctx.restore();
    };
    const box = (S, patch = {}, preview = null) => drawEditorCell(ctx, S, { ...makeCell(0, 0), ...patch }, preview);
    switch (id) {
        case "box":
            return one((S) => box(S));
        case "mystery":
            return one((S) => box(S, { isMystery: true }));
        case "arrow":
            return one((S) => box(S, { isArrow: true, arrowDirection: GridDirection.Top }));
        case "split":
            // Shown in three colours, as a compiled split box draws: grey with seams would read as a plain box.
            return one((S) => drawEditorCell(ctx, S, { ...makeCell(0, 0), isSplit: true }, MarbleColor.Yellow01, false, [MarbleColor.Red01, MarbleColor.Yellow01, MarbleColor.Blue02]));
        case "double":
            // Shown in two colours, as a compiled double box draws: grey on grey reads too close to a plain box.
            return one((S) => drawEditorCell(ctx, S, { ...makeCell(0, 0), isDouble: true }, MarbleColor.Blue02, false, null, MarbleColor.Orange));
        case "jumpingJack":
            return one((S) => drawEditorCell(ctx, S, { ...makeCell(0, 0), isJumpingJack: true }, MarbleColor.Blue02));
        case "dudBox":
            return one((S) => drawEditorCell(ctx, S, { ...makeCell(0, 0), isDudBox: true }, MarbleColor.Blue02));
        case "tunnel":
            return one((S) => box(S, { isTunnel: true, tunnelDirection: GridDirection.Bottom }));
        case "cleared":
            return one((S) => drawCleared(ctx, S));
        case "superTunnel":
            return one((S) => drawSuperTunnel(ctx, S, 0));
        case "toolBox":
            return one((S) => drawToolBox(ctx, S, 0));
        case "pipe":
            return one((S) => drawPipeIcon(ctx, S, 0));
        case "propeller":
            return one((S) => drawPropeller(ctx, S, null));
        case "ice":
            return one((S) => {
                box(S);
                drawIceCover(ctx, 0, 0, S, 2);
            });
        case "superMystery":
            return one((S) => drawSuperMysteryCover(ctx, 0, 0, S));
        case "gate":
            return strip(2.1, (cs) => drawGateWide(ctx, cs * 0.05, 0, cs * 2, cs));
        case "door":
            return strip(3, (cs) => drawSlidingDoor(ctx, [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 2, y: 0 }], 3, cs));
        case "bolt":
            return strip(3.15, (cs) => {
                ctx.translate(cs * 0.075, 0);
                drawBoltSprite(ctx, { x: 0, y: 0 }, { x: 2, y: 0 }, cs);
            });
        case "laser":
            return strip(3.5, (cs) => {
                ctx.translate(cs * 0.25, 0);
                drawLaserGateSprite(ctx, { x: 0, y: 0 }, { x: 2, y: 0 }, cs, 0);
            });
        case "connect":
            return strip(2, (cs) => {
                ctx.save();
                box(cs, {}, null);
                ctx.translate(cs, 0);
                box(cs, {}, null);
                ctx.restore();
                drawConnectorBar(ctx, cs * 0.5, cs * 0.5, cs * 1.5, cs * 0.5, cs * 0.28);
            });
        case "metalBox":
            return quad((cs) => drawMetalBox(ctx, 0, 0, cs * 2, cs * 2, 3, cs));
        case "elevator":
            return quad((cs) => drawElevator(ctx, 0, 0, cs * 2, cs * 2, cs));
        case "glassPanel":
            return quad((cs) => {
                const colours = [MarbleColor.Purple, MarbleColor.Pink, MarbleColor.Yellow01, MarbleColor.Green01];
                colours.forEach((c, i) => {
                    ctx.save();
                    ctx.translate((i % 2) * cs, Math.floor(i / 2) * cs);
                    box(cs, {}, c);
                    ctx.restore();
                });
                drawGlassPanel(ctx, 0, 0, cs * 2, cs * 2, [{ color: MarbleColor.Blue01, count: 1 }, { color: MarbleColor.Blue01, count: 2 }, { color: MarbleColor.Red01, count: 1 }, { color: MarbleColor.Orange, count: 2 }]);
            });
        case "lock":
            return quad((cs) => drawMetalRect(ctx, 0, 0, cs * 2, cs * 2, 0, cs * (2 / 3)));
        case "color":
            // The Colour tool paints boxes: four real boxes in four colours.
            return quad((cs) => {
                const colours = [MarbleColor.Red01, MarbleColor.Blue01, MarbleColor.Yellow01, MarbleColor.Green02];
                colours.forEach((c, i) => {
                    ctx.save();
                    ctx.translate((i % 2) * cs, Math.floor(i / 2) * cs);
                    box(cs, {}, c);
                    ctx.restore();
                });
            });
        case "select":
            // Four boxes, three of them inside a dashed box-select, and the pointer that drew it.
            return quad((cs) => {
                for (let i = 0; i < 4; i++) {
                    ctx.save();
                    ctx.translate((i % 2) * cs, Math.floor(i / 2) * cs);
                    box(cs);
                    ctx.restore();
                }
                ctx.save();
                ctx.fillStyle = "rgba(129,140,248,0.28)";
                ctx.fillRect(0, 0, cs * 2, cs);
                ctx.fillRect(0, cs, cs, cs);
                ctx.setLineDash([cs * 0.16, cs * 0.1]);
                ctx.lineWidth = Math.max(1.5, cs * 0.07);
                ctx.strokeStyle = "#c7d2fe";
                ctx.strokeRect(cs * 0.04, cs * 0.04, cs * 1.92, cs * 1.35);
                ctx.setLineDash([]);
                // The pointer, tip at the box-select's corner.
                const tx = cs * 1.96;
                const ty = cs * 1.39;
                const k = cs * 0.62;
                ctx.beginPath();
                ctx.moveTo(tx, ty);
                ctx.lineTo(tx, ty + k);
                ctx.lineTo(tx + k * 0.27, ty + k * 0.76);
                ctx.lineTo(tx + k * 0.5, ty + k * 1.08);
                ctx.lineTo(tx + k * 0.64, ty + k * 1.0);
                ctx.lineTo(tx + k * 0.42, ty + k * 0.68);
                ctx.lineTo(tx + k * 0.74, ty + k * 0.68);
                ctx.closePath();
                ctx.fillStyle = "#ffffff";
                ctx.fill();
                ctx.lineWidth = Math.max(1, cs * 0.05);
                ctx.strokeStyle = "#0f172a";
                ctx.stroke();
                ctx.restore();
            });
        default:
            return one((S) => box(S));
    }
}
export class MechanicIcon extends Component {
    constructor() {
        super(...arguments);
        this.canvasRef = createRef();
        this.stopRedraw = null;
    }
    componentDidMount() {
        this.draw();
        // The art is the game's sprites, decoded asynchronously: draw again once they are in.
        this.stopRedraw = onSpritesLoaded(() => this.draw());
    }
    componentWillUnmount() {
        this.stopRedraw?.();
    }
    componentDidUpdate() {
        this.draw();
    }
    draw() {
        const canvas = this.canvasRef.current;
        const ctx = canvas?.getContext("2d");
        if (!canvas || !ctx) {
            return;
        }
        const { id, width, height } = this.props;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, width, height);
        drawIcon(ctx, id, width, height);
    }
    render() {
        const { width, height } = this.props;
        return _jsx("canvas", { ref: this.canvasRef, style: { width, height }, className: "pointer-events-none block" });
    }
}
